import { type JSX, memo, useCallback } from "react";
import type { ParseKeys } from "i18next";
import { useTranslation } from "react-i18next";

import type { IInputProps, IInputType } from "@components/formControls";
import { Input } from "@components/formControls";
import type { IIconProps } from "@components/atoms/icons";
import type { IIconName } from "@components/icons";
import type { IFieldProps } from "@components/fastFields/field";
import { Field } from "@/components/fastFields/field/field";
import type { IExtends } from "@/utils";
import { checkFormikEqual } from "@/utils";
import { useReadOnly } from "@/hooks";

type ITypes = IExtends<
  ParseKeys<"form">,
  | "firstName"
  | "lastName"
  | "hfName"
  | "postalCode"
  | "website"
  | "email"
  | "address"
  | "licenseNumber"
  | "nationalCode"
  | "patientFileNumber"
  | "staffId"
  | "employeeCode"
  | "search"
  | "duaration"
  | "price"
  | "percentage"
  | "illnessName"
  | "reason"
  | "medicine"
  | "order"
  | "M.C.No"
  | "job_question"
  | "allergy_to"
  | "reaction"
  | "surgery_type"
  | "universityOfStudy"
  | "areaOfExpertise"
  | "workExperience"
  | "typeOfExperiments"
  | "nameOfImage"
  | "pattern_name"
  | "disease_name"
  | "symptomName"
  | "medicineType"
  | "optionalPatientWeight"
  | "optionalPatientHeight"
  | "discountCode"
  | "userType"
  | "subject"
  | "message"
  | "nickname"
  | "glucose"
  | "temperature"
  | "systolic"
  | "diastolic"
  | "pulse_rate"
  | "oximeter"
  | "weight"
  | "phone"
  | "landlineNumber"
  | "email_notif"
  | "income_percent"
  | "fax"
  | "count"
  | "optionalHonorsAndAwards"
  | "optionalMembership"
  | "optionalDoctorPosition"
  | "moneyAmount"
  | "mspName"
  | "serviceTitle"
  | "shabaNumber"
>;

const iconMap: { [key in ITypes]: IIconName | IIconProps } = {
  firstName: "UserRounded",
  lastName: "UserRounded",
  hfName: "Hospital",
  postalCode: "Mailbox",
  website: "Global",
  email: "Letter",
  licenseNumber: "MedalRibbonStar",
  address: { name: "Routing", type: "bold" },
  nationalCode: "Card",
  patientFileNumber: "Clipboard",
  staffId: "UserRounded",
  employeeCode: "UserRounded",
  search: "Magnifer",
  duaration: "AddSquare",
  percentage: "AddSquare",
  price: "DollarMinimalistic",
  medicine: "Pills",
  illnessName: "Pills",
  reason: "DropperMinimalistic",
  order: "TestTube",
  "M.C.No": "MedalRibbonStar",
  surgery_type: "DropperMinimalistic",
  job_question: "CaseRound",
  allergy_to: "Virus",
  reaction: "Virus",
  universityOfStudy: "Buildings",
  areaOfExpertise: "MedalStarSquare",
  workExperience: "Calendar",
  typeOfExperiments: "TestTube",
  nameOfImage: "Gallery",
  pattern_name: "JarOfPills",
  disease_name: "FacemaskCircle",
  symptomName: "FacemaskCircle",
  medicineType: "Pills",
  optionalPatientWeight: "Weigher",
  optionalPatientHeight: "RulerPen",
  discountCode: "TicketSale",
  userType: "UserRounded",
  subject: "LetterOpened",
  message: "LetterOpened",
  nickname: "UserRounded",
  glucose: "MedicalKit",
  temperature: "MedicalKit",
  systolic: "MedicalKit",
  diastolic: "MedicalKit",
  pulse_rate: "MedicalKit",
  oximeter: "MedicalKit",
  weight: "MedicalKit",
  phone: "Phone",
  landlineNumber: "PhoneCallingRounded",
  email_notif: "Letter",
  income_percent: "SaleSquare",
  fax: "PrinterMinimalistic",
  count: "AddCircle",
  optionalHonorsAndAwards: "MedalRibbonsStar",
  optionalMembership: "Card",
  optionalDoctorPosition: "Figma",
  moneyAmount: "BillList",
  mspName: "Hospital",
  serviceTitle: "Settings",
  shabaNumber: "Card",
};

interface IProps<T>
  extends Pick<IInputProps<string>, "inputType">,
    Required<Pick<IFieldProps<T>, "formik" | "name">>,
    Partial<Pick<IFieldProps<T>, "icon" | "title" | "isRequired">> {
  type: ITypes;
  disabled?: boolean;
  placeholder?: string;
  // dependents?: (keyof T)[]
  // dependencies?: (keyof T)[]
  readonly?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  onChange?: (val: string) => void;
  maxNumber?: number;
  minNumber?: number;
  maxLength?: number;
  noLabel?: boolean;
}

const getInputType = (
  inputType: IInputType | undefined,
  type: ITypes,
): IInputType => {
  if (inputType) return inputType;
  if (type === "phone" || type === "landlineNumber" || type === "fax")
    return "customNumber";
  if (type === "email") return "email";
  return "text";
};

export const FastInput = memo(
  function FastInput<T>(props: IProps<T>) {
    const { onChange, isLoading, isError, disabled } = props;
    const readonly = useReadOnly(props.readonly);
    const { t } = useTranslation("form");
    const title = props.title || props.type;
    const placeholder = props.placeholder || t(title);
    const name = props.name;
    const { values, setFieldValue, errors, touched, handleBlur } = props.formik;
    const value = values[name] as string;
    const inputType = getInputType(props.inputType, props.type);
    const icon = props.icon || iconMap[props.type];
    const changeHandler = useCallback(
      (val: string) => {
        onChange?.(val);
        setFieldValue(name.toString(), val);
      },
      [onChange, setFieldValue, name],
    );

    const isRequired = getFastInputRequired(
      props.isRequired || false,
      readonly || false,
      props.type,
    );

    return (
      <Field
        name={name}
        title={title}
        formik={props.formik}
        icon={icon}
        isRequired={isRequired}
        noLabel={props.noLabel}
      >
        <Input
          placeholder={getPlaceholder(props.type, placeholder)}
          id={name.toString()}
          onChangeValue={changeHandler}
          onBlur={handleBlur}
          value={value}
          validation={touched[name] && errors[name] ? "error" : undefined}
          readOnly={readonly}
          disabled={disabled}
          inputType={inputType}
          {...{ isLoading, isError }}
          maxNumber={props.maxNumber}
          maxLength={props.maxLength}
          minNumber={props.minNumber}
          dir={["email", "website"]?.includes(props.type) ? "ltr" : "rtl"}
        />
      </Field>
    );
  },
  (prevProps, nextProps) => checkFormikEqual(nextProps, prevProps),
) as <T>(props: IProps<T>) => JSX.Element;

const getPlaceholder = (type: ITypes, placeholder: string) => {
  if (type === "email") {
    return "example@gmail.com";
  }
  if (type === "website") {
    return "https://example.com";
  }
  return placeholder;
};

const getFastInputRequired = (
  isRequired: boolean,
  readonly: boolean,
  type: ITypes,
) => {
  if (import.meta.env.VITE_LANGUAGE === "fa") {
    return isRequired && !readonly;
  } else {
    if (type === "nationalCode") {
      return false;
    }
    return isRequired && !readonly;
  }
};
